/** Print the actual Lens board. Layout adjustments release dashboard frames for pagination;
 * figures, tables, prose, colours and typography come from the rendered report itself. */
import { sanitisePrintDocument } from './customView/customLensPrint';
import { lensPrintFontCss } from './print/lensPrintFonts';

export type LensPdfErrorCode = 'busy' | 'empty' | 'missing-cards' | 'stale';
export class LensPdfExportError extends Error {
  constructor(
    readonly code: LensPdfErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'LensPdfExportError';
  }
}

export type LensBoardPrintInput = {
  root: HTMLElement;
  title: string;
  periodCurrent?: string;
  periodComparison?: string;
  filters?: ReadonlyArray<string>;
  expectedCardCount: number;
  includeHeading?: boolean;
  isStale?: () => boolean;
  onProgress?: (done: number, total: number) => void;
  generatedAt?: Date;
};

// Copy appearance, not measured dashboard dimensions. Emotion rules can live only in CSSOM,
// and the scheduled renderer intentionally has no stylesheet or font network access.
const PRINT_PROPERTIES = [
  'display',
  'box-sizing',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'line-height',
  'letter-spacing',
  'text-align',
  'text-transform',
  'text-decoration',
  'white-space',
  'overflow-wrap',
  'word-break',
  'color',
  'background-color',
  'opacity',
  'visibility',
  'clip-path',
  'fill',
  'stroke',
  'stroke-width',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-radius',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'flex-direction',
  'flex-wrap',
  'align-items',
  'justify-content',
  'gap',
  'flex',
  'grid-column',
  'grid-row',
  'align-self',
  'justify-self',
  'vertical-align',
  'list-style-type',
  'list-style-position',
  'border-collapse',
  'border-spacing',
];
const CARD = '[data-lens-pdf-card]';
const OMIT =
  '[data-lens-pdf-ignore], [data-testid="card-action-tray"], .lens-card-resize-handle, .react-resizable-handle';

function releaseFrame(element: HTMLElement) {
  Object.assign(element.style, {
    position: 'static',
    transform: 'none',
    width: 'auto',
    minWidth: '0',
    maxWidth: 'none',
    height: 'auto',
    minHeight: '0',
    maxHeight: 'none',
    overflow: 'visible',
  });
}

/** Creates the same script-free, network-free print document used for scheduled and manual exports. */
export async function createLensBoardPrintDocument(input: LensBoardPrintInput): Promise<string> {
  const check = () => {
    if (input.isStale?.()) throw new LensPdfExportError('stale', 'The board changed while preparing print.');
  };
  check();
  await input.root.ownerDocument.fonts?.ready;
  const cards = Array.from(input.root.querySelectorAll<HTMLElement>(CARD));
  if (!cards.length) throw new LensPdfExportError('empty', 'There are no cards on the board to export.');
  if (cards.length !== input.expectedCardCount)
    throw new LensPdfExportError(
      'missing-cards',
      `Only ${cards.length} of ${input.expectedCardCount} cards are on the board.`,
    );

  const owner = input.root.ownerDocument;
  const view = owner.defaultView!;
  const doc = owner.implementation.createHTMLDocument(input.title);
  const clone = input.root.cloneNode(true) as HTMLElement;
  const originals = [input.root, ...Array.from(input.root.querySelectorAll<HTMLElement>('*'))];
  const copies = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('*'))];
  const paired = new Map(originals.map((node, index) => [node, copies[index]]));
  for (const original of originals) {
    const copy = paired.get(original)!;
    if (copy !== clone && !clone.contains(copy)) continue;
    const appearance = view.getComputedStyle(original);
    copy.removeAttribute('style');
    for (const property of PRINT_PROPERTIES) copy.style.setProperty(property, appearance.getPropertyValue(property));
    if (appearance.display === 'grid') {
      const columns = appearance.gridTemplateColumns
        .split(/\s+/)
        .map((value) => (/^\d+(?:\.\d+)?px$/.test(value) ? parseFloat(value) : 0));
      if (columns.length && columns.every((width) => width > 0))
        copy.style.gridTemplateColumns = columns.map((width) => `${width}fr`).join(' ');
    }
    // Preserve the available Admin font's fallback when the isolated printer cannot fetch fonts.
    copy.style.fontFamily = `${appearance.fontFamily || 'Arial'}, Arial, sans-serif`;
    if (
      appearance.display === 'none' ||
      appearance.visibility === 'hidden' ||
      appearance.visibility === 'collapse' ||
      appearance.opacity === '0' ||
      appearance.clipPath === 'inset(50%)' ||
      /rect\(0px[, ]+0px[, ]+0px[, ]+0px\)/.test(appearance.clip) ||
      original.matches(`${OMIT}, details:not([open])`)
    ) {
      copy.remove();
      continue;
    }
    // Internal DOM figures encode meaning in positioned bars and small SVGs. Keep
    // that geometry while dashboard/card frames are released separately below.
    if (original instanceof view.SVGElement && original.tagName.toLowerCase() === 'svg') {
      copy.style.width = appearance.width;
      copy.style.height = appearance.height;
    }
    if (appearance.position === 'absolute') {
      copy.style.position = 'absolute';
      const width = (original.offsetParent ?? original.parentElement)?.getBoundingClientRect().width ?? 0;
      for (const property of ['width', 'left', 'right']) {
        const value = appearance.getPropertyValue(property);
        const pixels = /^-?\d+(?:\.\d+)?px$/.test(value) ? parseFloat(value) : null;
        copy.style.setProperty(
          property,
          width && pixels !== null && Math.abs(pixels) > 1 ? `${(pixels / width) * 100}%` : value,
        );
      }
      for (const property of ['height', 'top', 'bottom'])
        copy.style.setProperty(property, appearance.getPropertyValue(property));
    } else if (
      appearance.position === 'relative' &&
      Array.from(original.children).some((child) => {
        const style = view.getComputedStyle(child);
        return (
          style.position === 'absolute' && style.display !== 'none' && style.opacity !== '0' && !child.matches(OMIT)
        );
      })
    ) {
      copy.style.position = 'relative';
      copy.style.height = appearance.height;
    }
    if (original instanceof view.HTMLCanvasElement) {
      if (!original.width || !original.height) throw new Error('A chart has not finished drawing.');
      const image = doc.createElement('img');
      image.src = original.toDataURL('image/png');
      image.alt = original.getAttribute('aria-label') || 'Report chart';
      image.style.cssText = copy.style.cssText;
      image.style.position = 'static';
      image.style.inset = 'auto';
      image.style.width = '100%';
      image.style.height = 'auto';
      image.style.aspectRatio = `${original.width} / ${original.height}`;
      copy.replaceWith(image);
      // ECharts absolutely positions its canvas inside measured wrappers. The
      // bitmap now participates in flow, so those wrappers must size from it.
      let parent = image.parentElement;
      while (parent && parent !== clone) {
        releaseFrame(parent);
        if (parent.hasAttribute('data-lens-card-body')) break;
        parent = parent.parentElement;
      }
    } else if (original instanceof view.HTMLImageElement) {
      if (!original.complete || !original.naturalWidth) throw new Error('A report image has not finished loading.');
      const bitmap = owner.createElement('canvas');
      bitmap.width = original.naturalWidth;
      bitmap.height = original.naturalHeight;
      const context = bitmap.getContext('2d');
      if (!context) throw new Error('A report image could not be copied.');
      context.drawImage(original, 0, 0);
      copy.setAttribute('src', bitmap.toDataURL('image/png'));
      copy.removeAttribute('srcset');
    }
    if (/(auto|scroll)/.test(`${appearance.overflowX} ${appearance.overflowY}`)) releaseFrame(copy);
  }
  clone
    .querySelectorAll('button, input, select, textarea, [role="tooltip"], .MuiTableSortLabel-icon')
    .forEach((node) => {
      // Sort headers are real report labels, not paper controls.
      if (node.matches('button') && node.closest('th')) return;
      node.remove();
    });
  clone
    .querySelectorAll<HTMLElement>(
      '[data-lens-card-frame], [data-lens-card-content], [data-lens-pdf-card], .MuiCard-root, .MuiCardContent-root, .MuiTableContainer-root',
    )
    .forEach(releaseFrame);
  clone
    .querySelectorAll<HTMLElement>('[data-lens-card-body], .MuiCard-root, .MuiCardContent-root')
    .forEach((element) => {
      releaseFrame(element);
      element.style.display = 'block';
    });
  // Tables need their complete row flow; preserve their visible row-limit notices too.
  for (const table of Array.from(clone.querySelectorAll('table'))) {
    let parent = table.parentElement;
    while (parent && parent !== clone) {
      releaseFrame(parent);
      if (parent.matches(CARD)) break;
      parent = parent.parentElement;
    }
  }
  let landscape = Array.from(clone.querySelectorAll('table')).some(
    (table) => table.querySelectorAll('thead tr:first-child th').length > 7,
  );
  for (const grid of Array.from(input.root.querySelectorAll<HTMLElement>('.react-grid-layout'))) {
    const printed = paired.get(grid)!;
    releaseFrame(printed);
    printed.style.display = 'grid';
    printed.style.gridTemplateColumns = 'repeat(12, minmax(0, 1fr))';
    printed.style.gap = '3mm';
    printed.style.alignItems = 'start';
    const bounds = grid.getBoundingClientRect();
    const frames = Array.from(grid.children).filter((frame): frame is HTMLElement => frame instanceof view.HTMLElement);
    frames.sort(
      (a, b) =>
        a.getBoundingClientRect().top - b.getBoundingClientRect().top ||
        a.getBoundingClientRect().left - b.getBoundingClientRect().left,
    );
    for (const frame of frames) {
      const item = paired.get(frame)!;
      const rect = frame.getBoundingClientRect();
      const span = bounds.width ? Math.max(1, Math.min(12, Math.round((rect.width / bounds.width) * 12))) : 12;
      const start = bounds.width
        ? Math.max(1, Math.min(13 - span, Math.round(((rect.left - bounds.left) / bounds.width) * 12) + 1))
        : 1;
      releaseFrame(item);
      item.style.gridColumn = `${start} / span ${span}`;
      if (span < 12) landscape = true;
      // A long table uses the page width instead of becoming tiny beside another card.
      if (frame.querySelector('table')) item.style.gridColumn = '1 / -1';
      printed.append(item);
    }
  }
  releaseFrame(clone);
  // Block flow fragments across pages; a flex root can move an entire section
  // to the next sheet even when its first row fits beside the title.
  clone.style.display = 'block';
  const base = view.getComputedStyle(input.root);
  doc.body.style.fontFamily = `${base.fontFamily || 'Arial'}, Arial, sans-serif`;
  doc.body.style.color = base.color;
  const heading = doc.createElement('h1');
  heading.textContent = input.title || 'Lens';
  heading.style.cssText = 'font-size:24px;line-height:1.2;margin:0 0 2mm;font-weight:600';
  const summary = doc.createElement('p');
  summary.textContent = [
    input.periodCurrent,
    input.periodComparison ? `Compared with ${input.periodComparison}` : '',
    ...(input.filters ?? []),
  ]
    .filter(Boolean)
    .join(' · ');
  summary.style.cssText = 'font-size:12px;line-height:1.5;margin:0 0 4mm';
  const generated = doc.createElement('p');
  generated.textContent = `Generated ${new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'medium', timeStyle: 'short' }).format(input.generatedAt ?? new Date())} Sydney time`;
  generated.style.cssText = 'font-size:10px;line-height:1.4;margin:0 0 3mm';
  if (input.includeHeading !== false) doc.body.append(heading, summary, generated);
  doc.body.append(clone);
  const style = doc.createElement('style');
  style.textContent = `
    @page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 6mm; }
    html, body { margin: 0; padding: 0; width: auto; min-height: 0; height: auto; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    h1, h2, h3, h4 { break-after: avoid; }
    [data-lens-pdf-section] { margin-bottom: 4mm; }
    [data-lens-pdf-card]:not([data-lens-pdf-card-type="table"]):not([data-lens-pdf-card-type="insight"]) { break-inside: avoid; }
    table { width: 100%; table-layout: auto; border-collapse: collapse; }
    thead { display: table-header-group; } tfoot { display: table-footer-group; }
    tr { break-inside: avoid; } th, td { white-space: normal; overflow-wrap: anywhere; }
    img, svg { max-width: 100%; height: auto; break-inside: avoid; }
  `;
  doc.head.append(style);
  cards.forEach((_card, index) => input.onProgress?.(index + 1, cards.length));
  const fontCss = /Suisse/.test(doc.documentElement.outerHTML) ? await lensPrintFontCss() : '';
  check();
  return sanitisePrintDocument(`<!doctype html>${doc.documentElement.outerHTML}`, input.title, fontCss);
}
